import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useEffect } from 'react';
import { autoLogout, refreshToken, selectCurrentUser, selectCurrentToken } from './features/auth/authSlice';
import { setLogoutCallback } from './utils/apiClient';
import tokenExpirationManager from './utils/tokenExpirationManager';
import { initializeDocumentTypes } from './features/documents/documentService';
import { ToastProvider } from './contexts/ToastContext';
import { EnhancedToastProvider } from './contexts/EnhancedToastContext';
// Error Boundary Components
import ErrorBoundary from './components/ErrorBoundary';
import ClaimErrorBoundary from './components/ClaimErrorBoundary';
// Page Components
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PasswordChangeForm from './components/auth/PasswordChangeForm';
import RequirePasswordChange from './components/auth/RequirePasswordChange';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import UserManagement from './pages/UserManagement';
import DocumentManagement from './pages/DocumentManagement';
import ClaimSubmission from './components/claims/ClaimSubmission';
import ClaimsList from './pages/ClaimsList';
import ClaimDetail from './pages/ClaimDetail';
import SubmitClaim from './pages/claims/SubmitClaim';
import AuditLogs from './pages/admin/AuditLogs/index';
import SlamsSyncManagement from './pages/admin/SlamsSyncManagement';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Header from './components/layout/Header';
// SLA Components
import SlaDashboard from './components/sla/SlaDashboard';
import SlaAccessGuide from './components/sla/SlaAccessGuide';
// Finance Components
import PaymentManagement from './components/finance/PaymentManagement';
// Debug Components (Development Only)
import ErrorBoundaryDemo from './components/debug/ErrorBoundaryDemo';

// SLA Component Wrappers to connect with Redux user data
const SlaDashboardWrapper = () => {
  const user = useSelector(selectCurrentUser);
  return <SlaDashboard user={user} />;
};

const SlaAccessGuideWrapper = () => {
  const user = useSelector(selectCurrentUser);
  return <SlaAccessGuide user={user} />;
};

function App() {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser, shallowEqual);
  const token = useSelector(selectCurrentToken);
  const isAuthenticated = !!user && !!token;
  
  // Set up token expiration monitoring and global logout handling
  useEffect(() => {
    // Define logout handler
    const handleAutoLogout = () => {
      dispatch(autoLogout());
      
      // Stop monitoring after logout
      tokenExpirationManager.stopMonitoring();
    };

    // Define proactive refresh handler
    const handleProactiveRefresh = async () => {
      try {
        const storedRefreshToken = localStorage.getItem('refreshToken');
        if (storedRefreshToken) {
          await dispatch(refreshToken()).unwrap();
          console.log('✅ Proactive token refresh completed');
        }
      } catch (error) {
        console.error('❌ Proactive refresh failed:', error);
        throw error; // Let tokenExpirationManager handle the fallback
      }
    };

    if (isAuthenticated && token) {
      // Set up global logout callback for API client only when authenticated
      setLogoutCallback(handleAutoLogout);
      
      // Start monitoring token expiration with proactive refresh
      tokenExpirationManager.startMonitoring(handleAutoLogout, handleProactiveRefresh);
    } else {
      // Clear logout callback and stop monitoring if not authenticated
      setLogoutCallback(null);
      tokenExpirationManager.stopMonitoring();
    }

    // Cleanup on unmount
    return () => {
      tokenExpirationManager.stopMonitoring();
    };
  }, [isAuthenticated, token, dispatch]);

  // Update token monitoring when token changes
  useEffect(() => {
    if (isAuthenticated && token) {
      tokenExpirationManager.updateToken();
      // Initialize document types when user is authenticated
      initializeDocumentTypes();
    }
  }, [token, isAuthenticated]);
  
  // Debugging for auth state
  useEffect(() => {
    // Console log removed for production
  }, [isAuthenticated, user, token]);

  return (
    <ErrorBoundary componentName="Application Root" showDetails={true}>
      <ToastProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <div className="min-h-screen bg-gray-50 flex flex-col">
            {isAuthenticated && (
              <ErrorBoundary componentName="Header Navigation" fallbackMessage="Navigation is temporarily unavailable.">
                <Header />
              </ErrorBoundary>
            )}
            <main className="flex-grow px-4 py-6 sm:p-6 md:py-10">
              <Routes>
                {/* Login Route - No error boundary needed for login */}
                <Route path="/login" element={<Login />} />
                
                {/* Forgot Password Route - Public */}
                <Route path="/forgot-password" element={<ForgotPassword />} />
                
                {/* Reset Password Route - Public */}
                <Route path="/reset-password" element={<ResetPassword />} />
                
                {/* Password Change Route - For first-time users */}
                <Route 
                  path="/change-password" 
                  element={
                    <RequirePasswordChange>
                      <ErrorBoundary componentName="Password Change" fallbackMessage="Password change form is temporarily unavailable.">
                        <PasswordChangeForm />
                      </ErrorBoundary>
                    </RequirePasswordChange>
                  } 
                />
                
                {/* Dashboard - General Error Boundary */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary componentName="Dashboard" fallbackMessage="Dashboard is temporarily unavailable.">
                        <Dashboard />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Claims Routes - Specialized Claims Error Boundary */}
                <Route 
                  path="/claims/submit" 
                  element={
                    <ProtectedRoute requiredRoles={['HR', 'Broker', 'CRM']}>
                      <ClaimErrorBoundary section="Claim Submission" customMessage="The claim submission form encountered an issue. Your progress has been saved.">
                        <ClaimSubmission />
                      </ClaimErrorBoundary>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/claims/my-claims" 
                  element={
                    <ProtectedRoute requiredRoles={['HR', 'Broker', 'CRM']}>
                      <ClaimErrorBoundary section="My Claims" customMessage="Unable to load your claims at the moment.">
                        <ClaimsList />
                      </ClaimErrorBoundary>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/claims" 
                  element={
                    <ProtectedRoute requiredRoles={['Claims_Officer', 'Approver', 'Finance', 'HR', 'Broker', 'CRM']}>
                      <ClaimErrorBoundary section="Claims Management" customMessage="Claims management system is temporarily unavailable.">
                        <ClaimsList />
                      </ClaimErrorBoundary>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/claims/submit" 
                  element={
                    <ProtectedRoute requiredRoles={['HR', 'Broker', 'CRM', 'Claims_Officer']}>
                      <ClaimErrorBoundary section="New Claim Submission">
                        <SubmitClaim />
                      </ClaimErrorBoundary>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/claims/:claimId" 
                  element={
                    <ProtectedRoute>
                      <ClaimErrorBoundary section="Claim Details" customMessage="Unable to load claim details. Please try refreshing the page.">
                        <ClaimDetail />
                      </ClaimErrorBoundary>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/claims/:claimId/documents" 
                  element={
                    <ProtectedRoute>
                      <Navigate to="/claims/:claimId" replace />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Document Management - Error Boundary for Document Operations */}
                <Route 
                  path="/documents" 
                  element={
                    <ProtectedRoute requiredRoles={['Claims_Officer', 'Approver']}>
                      <ErrorBoundary componentName="Document Management" fallbackMessage="Document management system is experiencing issues.">
                        <DocumentManagement />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  } 
                />
                
                {/* User Management - Admin Error Boundary */}
                <Route 
                  path="/admin/users" 
                  element={
                    <ProtectedRoute requiredRoles={['Admin']}>
                      <ErrorBoundary componentName="User Management" fallbackMessage="User management interface is temporarily unavailable.">
                        <UserManagement />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/users" 
                  element={
                    <ProtectedRoute requiredRoles={['Admin']}>
                      <ErrorBoundary componentName="User Management" fallbackMessage="User management interface is temporarily unavailable.">
                        <UserManagement />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  } 
                />

                {/* Claims Processing Routes - Claims Error Boundaries */}
                <Route 
                  path="/claims/process" 
                  element={
                    <ProtectedRoute requiredRoles={['Claims_Officer']}>
                      <ClaimErrorBoundary section="Claims Processing" customMessage="Claims processing interface is temporarily unavailable.">
                        <ClaimsList />
                      </ClaimErrorBoundary>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/claims/approve" 
                  element={
                    <ProtectedRoute requiredRoles={['Approver']}>
                      <ClaimErrorBoundary section="Claims Approval" customMessage="Claims approval interface is temporarily unavailable.">
                        <ClaimsList />
                      </ClaimErrorBoundary>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/claims/payments" 
                  element={
                    <ProtectedRoute requiredRoles={['Finance']}>
                      <ClaimErrorBoundary section="Approved Claims" customMessage="Claims payment interface is temporarily unavailable.">
                        <ClaimsList />
                      </ClaimErrorBoundary>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Analytics Route */}
                <Route 
                  path="/analytics" 
                  element={
                    <ProtectedRoute requiredRoles={['Admin']}>
                      <ErrorBoundary componentName="Analytics Dashboard">
                        <div className="min-h-screen bg-gray-50 py-8">
                          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <h1 className="text-3xl font-bold text-gray-900 mb-8">System Analytics</h1>
                            <div className="bg-white shadow rounded-lg p-8 text-center">
                              <p className="text-gray-600">Analytics dashboard coming soon</p>
                            </div>
                          </div>
                        </div>
                      </ErrorBoundary>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Audit Logs - Requires Special Error Handling */}
                <Route 
                  path="/audit-logs" 
                  element={
                    <ProtectedRoute requiredPermission="audit_logs:read">
                      <ErrorBoundary componentName="Audit Logs" fallbackMessage="Audit logs are temporarily unavailable.">
                        <AuditLogs />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  } 
                />

                {/* SLAMS Sync Management - Admin Only */}
                <Route 
                  path="/admin/slams-sync" 
                  element={
                    <ProtectedRoute requiredRoles={['Admin']}>
                      <ErrorBoundary componentName="SLAMS Sync Management" fallbackMessage="SLAMS sync management interface is temporarily unavailable.">
                        <EnhancedToastProvider>
                          <SlamsSyncManagement />
                        </EnhancedToastProvider>
                      </ErrorBoundary>
                    </ProtectedRoute>
                  } 
                />

                {/* SLA Management Routes */}
                <Route 
                  path="/sla-dashboard" 
                  element={
                    <ProtectedRoute requiredRoles={['Claims_Officer', 'Approver', 'Finance', 'Admin']}>
                      <ErrorBoundary componentName="SLA Dashboard" fallbackMessage="SLA monitoring dashboard is temporarily unavailable.">
                        <SlaDashboardWrapper />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/sla-guide" 
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary componentName="SLA Access Guide" fallbackMessage="SLA access guide is temporarily unavailable.">
                        <SlaAccessGuideWrapper />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Financial Routes */}
                <Route 
                  path="/payments" 
                  element={
                    <ProtectedRoute requiredRoles={['Finance']}>
                      <ErrorBoundary componentName="Payment Management">
                        <PaymentManagement />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/reports" 
                  element={
                    <ProtectedRoute requiredRoles={['Finance']}>
                      <ErrorBoundary componentName="Financial Reports">
                        <div className="min-h-screen bg-gray-50 py-8">
                          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <h1 className="text-3xl font-bold text-gray-900 mb-8">Financial Reports</h1>
                            <div className="bg-white shadow rounded-lg p-8 text-center">
                              <p className="text-gray-600">Financial reports interface coming soon</p>
                            </div>
                          </div>
                        </div>
                      </ErrorBoundary>
                    </ProtectedRoute>
                  } 
                />
                
                {/* Default and Catch-all Routes */}
                <Route 
                  path="/" 
                  element={isAuthenticated ? <Navigate to="/dashboard" /> : <Navigate to="/login" />} 
                />

                {/* Debug Route - Development Only */}
                {import.meta.env.DEV && (
                  <Route 
                    path="/debug/error-boundaries" 
                    element={
                      <ProtectedRoute requiredRoles={['Admin']}>
                        <ErrorBoundary componentName="Debug Tools">
                          <ErrorBoundaryDemo />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } 
                  />
                )}
                
                <Route 
                  path="*" 
                  element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} 
                />
              </Routes>
            </main>
          </div>
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
